import { useEffect, useState, type CSSProperties } from "react";

interface DonutChartProps {
  /** Numerator. */
  value: number;
  /** Denominator (defaults to 1 — i.e. value is treated as 0..1). */
  total?: number;
  /** Stroke color of the progress arc. */
  color?: string;
  /** Background ring color. */
  trackColor?: string;
  /** Outer SVG size in pixels. Default 120. */
  size?: number;
  /** Stroke width. Default 10. */
  strokeWidth?: number;
  /** When true the center value text is rendered. Default true. */
  showValue?: boolean;
  /** Override the rendered center text (e.g. show a percentage). */
  label?: string;
  /** Animate the count-up of the center number. Default true. */
  animateValue?: boolean;
  className?: string;
}

function useAnimatedNumber(target: number, enabled: boolean, duration = 900) {
  const [display, setDisplay] = useState(enabled ? 0 : target);

  useEffect(() => {
    if (!enabled) {
      setDisplay(target);
      return;
    }

    let frame = 0;
    const start = performance.now();
    const from = 0;

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (t < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, duration, enabled]);

  return display;
}

/**
 * Animated donut chart used across the app. The progress arc transitions
 * from an empty state to its target on mount and on subsequent value changes,
 * and the center number counts up from 0.
 */
export function DonutChart({
  value,
  total = 1,
  color = "#120c80",
  trackColor = "#e8ecf4",
  size = 120,
  strokeWidth = 10,
  showValue = true,
  label,
  animateValue = true,
  className = "",
}: DonutChartProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = total > 0 ? Math.min(1, Math.max(0, value / total)) : 0;
  const targetOffset = circumference * (1 - ratio);

  // Start at "empty" so the transition is visible on first paint.
  const [animatedOffset, setAnimatedOffset] = useState(circumference);
  useEffect(() => {
    const id = requestAnimationFrame(() => setAnimatedOffset(targetOffset));
    return () => cancelAnimationFrame(id);
  }, [targetOffset]);

  const animatedValue = useAnimatedNumber(value, animateValue);

  const center = size / 2;
  const styleVars = {
    "--donut-circumference": `${circumference}`,
    "--donut-target-offset": `${targetOffset}`,
  } as CSSProperties;

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className={`events-donut-chart ${className}`.trim()}
      role="img"
      aria-label={label ?? `${value} of ${total}`}
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={trackColor}
        strokeWidth={strokeWidth}
      />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={animatedOffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${center} ${center})`}
        className="events-donut-chart__progress"
        style={styleVars}
      />
      {showValue ? (
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dominantBaseline="central"
          className="events-donut-chart__value"
        >
          {label ?? animatedValue}
        </text>
      ) : null}
    </svg>
  );
}
